<script lang="ts">
export default {
  name: 'QuoteCard',
};
</script>

<script setup lang="ts">
import { ref, computed } from 'vue';
import { Pencil, EllipsisVertical, Copy, Trash2, Grip } from 'lucide-vue-next';
import { Quote, calculateQuotePPM } from '@/types/Quote';
import { filters } from '@/utilities/Filters';
import AppButton from '@/components/v2/AppButton.vue';
import MenuDropdown from '@/components/v2/MenuDropdown.vue';
import type { MenuItem } from '@/components/v2/MenuDropdown.vue';
import Chip from '@/components/v2/Chip.vue';
import ConfirmModal from '@/components/ConfirmModal.vue';

const props = defineProps<{
  quote: Quote;
  handleDelete: (quoteId: string | number) => Promise<void>;
  editAction?: (quote: Quote) => void;
  copyAction?: (quote: Quote) => void;
  displayKey: number;
}>();

const deleteDialog = ref(false);
const deleting = ref(false);

const ppm = computed(() => calculateQuotePPM(props.quote));

const menuItems = computed<MenuItem[]>(() => {
  const items: MenuItem[] = [];
  if (props.editAction) {
    items.push({
      label: 'Edit',
      handler: () => props.editAction?.(props.quote),
      icon: Pencil,
      textColorClass: 'tw:text-error',
      iconColorClass: 'tw:text-error',
      testId: `edit-quote-${props.quote.quoteId}`,
    });
  }
  items.push({
    label: 'Delete',
    handler: () => (deleteDialog.value = true),
    icon: Trash2,
    textColorClass: 'tw:text-error',
    iconColorClass: 'tw:text-error',
    testId: `delete-quote-${props.quote.quoteId}`,
  });
  return items;
});

async function doDelete(): Promise<boolean> {
  deleting.value = true;
  try {
    await props.handleDelete(props.quote.quoteId?.toString() ?? '');
    deleteDialog.value = false;
    return true;
  } finally {
    deleting.value = false;
  }
}
</script>

<template>
  <div :key="displayKey">
    <div
      class="quote-card tw:relative tw:mb-4 tw:rounded-md tw:border tw:border-solid tw:border-gray-400 tw:p-4"
      v-bind:class="{ incumbent: quote.bound }"
      :data-testid="`quote-card-${quote.quoteId}`">
      <div class="card-header">
        <div class="card-title">
          {{ quote.insurerGroup || quote.wholesalerGroup }}
        </div>

        <div class="quote-header-actions tw:flex tw:items-center tw:gap-0">
          <AppButton
            variant="ghost"
            size="sm"
            class="tw:btn-square"
            :data-testid="`quote-copy-${quote.quoteId}`"
            @click="copyAction && copyAction(quote)">
            <Copy :size="16" />
          </AppButton>
          <MenuDropdown
            :items="menuItems"
            size="sm">
            <template #action>
              <button
                type="button"
                tabindex="0"
                class="tw:btn tw:btn-square tw:btn-ghost tw:btn-sm"
                :data-testid="`quote-actions-${quote.quoteId}`">
                <EllipsisVertical :size="20" />
              </button>
            </template>
          </MenuDropdown>
        </div>
      </div>

      <template v-if="!quote.declined">
        <dfn>Limit</dfn>
        <div>{{ filters.compactMoney(quote.aggregateLimit) }}</div>

        <dfn>PPM</dfn>
        <div>{{ filters.compactMoney(ppm) }}</div>
      </template>
      <template v-else>
        <dfn>Declined</dfn>
        <div>{{ quote.declinedReason }}</div>
      </template>

      <Chip
        v-if="quote.lowest"
        label="lowest"
        size="sm"
        class="tw:mr-1 tw:mb-1"
        color-class="tw:bg-success tw:text-white" />
      <Chip
        v-if="quote.incumbent"
        label="incumbent"
        size="sm"
        class="tw:mr-1 tw:mb-1"
        color-class="tw:bg-secondary tw:text-white" />
      <Chip
        v-if="quote.admitted"
        label="admitted"
        size="sm"
        class="tw:mr-1 tw:mb-1"
        color-class="tw:bg-warning tw:text-white" />

      <Grip
        :size="14"
        class="quote-card-drag-handle tw:text-gray-400" />
    </div>

    <ConfirmModal
      v-if="deleteDialog"
      title="Delete Quote"
      subtitle=""
      :action="doDelete"
      confirm-text="Delete"
      cancel-text="Cancel"
      size="md"
      :show="true"
      :submit-loading="deleting"
      @closed="deleteDialog = false">
      <template #default>
        <p class="tw:text-base tw:text-gray-700">Are you sure you want to delete this quote?</p>
      </template>
    </ConfirmModal>
  </div>
</template>

<style scoped>
.quote-card {
  position: relative;

  min-height: 4rem;
  margin: 1rem;
  padding: 0.5rem 0;

  border-radius: 4px;

  font-weight: bold;

  text-align: center;

  cursor: grab;
  background: #ffffff;
}

.incumbent {
  outline: 1px solid rgb(34 197 94);
}

.quote-card dfn {
  position: relative;
  text-transform: uppercase;
  font-style: normal;
  font-size: 0.8em;
  font-weight: normal;
  color: rgb(107 114 128);
}

.quote-card:active {
  cursor: grabbing;
}

.quote-card .card-header {
  display: grid;
  grid-template-columns: 1fr auto;
  grid-column-gap: 1rem;
  align-items: center;
  align-content: center;

  font-size: 1.1rem;
  font-weight: bold;
  border-bottom: 1px solid rgb(229 231 235);
  padding-bottom: 0.4rem;
}

.quote-card-drag-handle {
  position: absolute;
  bottom: 2px;
  right: 2px;
}

.card-title {
  padding-left: 1rem;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  text-align: left;
  max-width: 15rem;
  min-width: 6rem;
}
</style>
